import { useEffect, useState, useRef } from "react";

import { defaultScreenWidth, defaultShopify, useStore } from "@state/store";
import { isBrowser } from "./helper";
import {
  MOBILE_BREAKPOINT,
  TABLET_BREAKPOINT,
  LAPTOP_BREAKPOINT,
  SMALL_LAPTOP_BREAKPOINT,
  isEveryday,
} from "./constants";
import { BundleProduct, ScreenWidth } from "@state/types";
import { toast } from "react-toastify";
import { CheckoutLineItemInput } from "shopify-storefront-api-typings";
import { sanityClient } from "@lib/sanityClient";

// check if component has been mounted
export function useHasMounted() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!mounted) {
      setMounted(true);
    }
  }, []);

  if (!mounted) {
    return false;
  }

  return mounted;
}

// event listener hook
export function useEventListener(
  eventName: string,
  handler: (args: any) => void,
) {
  if (!isBrowser()) {
    return;
  }
  const savedHandler = useRef<typeof handler>();

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    const isSupported = window && window.addEventListener;
    if (!isSupported) return;

    //@ts-ignore
    const eventListener = (event: any) => savedHandler.current(event);
    window.addEventListener(eventName, eventListener);

    return () => {
      window.removeEventListener(eventName, eventListener);
    };
  }, [eventName, window]);
}

// checks screen width
export function useCheckScreenWidth(): ScreenWidth {
  if (!isBrowser()) {
    return defaultScreenWidth;
  }
  const [screenWidth, setScreenWidth] = useState(defaultScreenWidth);
  const hasMounted = useHasMounted();

  const checkScreenWidth = () => {
    if (window.innerWidth <= MOBILE_BREAKPOINT) {
      setScreenWidth({
        ...defaultScreenWidth,
        isMobileWidth: true,
      });
      return;
    }
    if (window.innerWidth <= TABLET_BREAKPOINT) {
      setScreenWidth({
        ...defaultScreenWidth,
        isTabletWidth: true,
      });
      return;
    }
    if (window.innerWidth <= SMALL_LAPTOP_BREAKPOINT) {
      setScreenWidth({
        ...defaultScreenWidth,
        isSmallLaptopWidth: true,
      });
      return;
    }
    if (window.innerWidth <= LAPTOP_BREAKPOINT) {
      setScreenWidth({
        ...defaultScreenWidth,
        isLaptopWidth: true,
      });
      return;
    }
    setScreenWidth({
      ...defaultScreenWidth,
    });
    return;
  };

  useEventListener("resize", checkScreenWidth);

  useEffect(() => {
    checkScreenWidth();
  }, []);

  useEffect(() => {
    if (hasMounted) {
      checkScreenWidth();
    }
  }, [hasMounted]);

  return screenWidth;
}

//bundles
export function useUpdateProductToBundle(
  bundleId: string | undefined | null,
  product: BundleProduct | undefined,
  lineId: string | null | undefined,
  // removeProduct: Boolean = false
) {
  const { bundles, setBundles } = useStore();
  const [products, setProducts] = useState<BundleProduct[]>();
  const bundle = bundles?.find(bundle => bundle.id === bundleId);

  useEffect(() => {
    if (products !== undefined && bundle && bundles?.length) {
      const newBundles = bundles.map(bundleObj => {
        if (bundleId === bundleObj.id) {
          return {
            ...bundleObj,
            products,
          };
        }
        return bundleObj;
      });
      if (newBundles) {
        useStore.setState({ bundles: newBundles });
        // setBundles({ bundles: newBundles });
      }
    }
  }, [products]);

  const handleAddToBundle = (shouldIncrease?: boolean) => {
    if (bundle == null) return;

    const productsFromState = bundle.products;
    if (productsFromState == null) return;

    //TODO: Pass in decrement / remove prop

    if (productsFromState?.length && lineId && !shouldIncrease) {
      const filteredProducts = productsFromState.filter(
        item => item.lineId !== lineId,
      );
      setProducts(filteredProducts);

      toast.success("Product removed from bundle");
      return;
    }

    if (productsFromState.length == bundle.totalProductsAllowed) {
      toast.error(
        "You have reached the maximum products allowed in this bundle",
      );
      return;
    }

    if (productsFromState?.length && product) {
      setProducts([...productsFromState, product]);
      toast.success("Product added to bundle");
      return;
    }

    if (product) {
      setProducts([product]);
    }
  };

  return handleAddToBundle;
}

export const checkProductExistsInBundle = (
  bundleId: Queries.Maybe<string> | undefined,
  productId: Queries.Maybe<string> | undefined,
) => {
  const { bundles } = useStore();
  const [exists, setExists] = useState<BundleProduct>();

  useEffect(() => {
    const bundle = bundles?.find(bundle => bundle.id === bundleId);
    const foundProduct = bundle?.products?.find(
      product => product.id == productId,
    );
    setExists(foundProduct);
  }, [bundles]);

  return exists;
};

export const useMaximumBundleQuantity = (bundleId: string) => {
  const { bundles } = useStore();
  const [maximumItems, setMaximumItems] = useState<number>();

  useEffect(() => {
    const bundle = bundles?.find(bundle => bundle.id === bundleId);
    if (bundle != null || bundle != undefined) {
      const products = bundle.products?.length;
      const maximum = bundle.totalProductsAllowed;

      if (products != undefined && products > 0) {
        const remaining = Number(maximum) - products;
        setMaximumItems(remaining);
      } else {
        setMaximumItems(maximum);
      }
    }
  }, [bundles]);

  useEffect(() => {}, [maximumItems]);

  return maximumItems;
};

export const useAddToCart = async (
  lineItems: CheckoutLineItemInput[],
  shopify: any,
  openCartDrawer: () => void,
  closeModal: () => void,
) => {
  const data = {
    lines: lineItems,
    cartId: shopify?.cart?.id,
    customer: shopify?.customer,
  };
  const res = await fetch(`/api/cartLinesAdd`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  try {
    const response = await res.json();

    const userErrors = Array.isArray(response.error) ? response.error : [];
    if (userErrors.length > 0) {
      if (userErrors[0]?.field?.[0] === "cartId") {
        useStore.setState({
          shopify: {
            ...shopify,
            cart: defaultShopify.cart,
          },
        });
        toast.error(
          "Your previous cart has expired. Please refresh your page and try again.",
        );
        return;
      }

      toast.error(
        "Something went wrong when adding the product to cart. Please try again.",
      );
      console.error(response.error);
      return;
    }

    if (response.err) {
      console.error("Cart API error:", response.err);
      toast.error(
        "Something went wrong when adding the product to cart. Please try again.",
      );
      return;
    }

    if (response.cart) {
      useStore.setState({
        shopify: {
          ...shopify,
          cart: response.cart,
        },
      });
      openCartDrawer();
      closeModal();
    }
  } catch (err) {
    console.error("catch error", err);
    toast.error(
      "Something went wrong when adding the product to cart. Please try again.",
    );
    throw err;
  }
};

export const usePinterestTag = () => {
  useEffect(() => {
    if (!isBrowser()) return;
    const script = document.createElement("script");
    script.innerHTML = `
      !function(e){if(!window.pintrk){window.pintrk = function () {
      window.pintrk.queue.push(Array.prototype.slice.call(arguments))};var
        n=window.pintrk;n.queue=[],n.version="3.0";var
        t=document.createElement("script");t.async=!0,t.src=e;var
        r=document.getElementsByTagName("script")[0];
        r.parentNode.insertBefore(t,r)}}("https://s.pinimg.com/ct/core.js");
      pintrk('load', '2612796353029', {em: '<user_email_address>'});
      pintrk('page');
    `;
    script.type = "text/javascript";
    script.async = true;
    document.body.appendChild(script);

    const noscript = document.createElement("noscript");
    noscript.innerHTML = `
      <img height="1" width="1" style="display:none;" alt=""
        src="https://ct.pinterest.com/v3/?event=init&tid=2612796353029&pd[em]=<hashed_email_address>&noscript=1" />
    `;
    document.body.appendChild(noscript);

    return () => {
      document.body.removeChild(script);
      document.body.removeChild(noscript);
    };
  }, []);
};

export const returnHomepageFeatureProducts = (ids: any) => {
  const [products, setProducts] = useState();

  const returnProducts = async () => {
    const idsOnly = ids.map((item: any) => item._id);
    console.log(idsOnly);

    const products = await sanityClient.fetch(
      `*[_type == "product" && _id in $idsOnly]{
        ...,
        content {
          ...,        
          main {
            ...,
            featuredAllergen->{ title, icon-> { asset-> { url } } }
          },
        },
        "category": *[_type == "category" && references(^._id)][0]{
          categoryColour
        }
      }`,
      { idsOnly },
    );
    if (Boolean(products?.length)) {
      setProducts(products);
    }
  };

  useEffect(() => {
    returnProducts();
    // console.log(ids);
  }, []);

  return products;
};

/**
 *  Check if page has completely loaded
 * @returns
 */
export default function usePageLoaded() {
  const isBrowser = typeof window !== "undefined";
  const [shouldLoad, setShouldLoad] = useState(
    isBrowser && document.readyState === "complete",
  );

  useEffect(() => {
    if (!isBrowser || shouldLoad) return;

    const handleShouldLoad = () => setShouldLoad(true);

    window.addEventListener("load", handleShouldLoad);

    return () => window.removeEventListener("load", handleShouldLoad);
  }, [isBrowser, shouldLoad]);

  return shouldLoad;
}

export function useDataFromSanity(query: Queries.Maybe<string>, params?: any) {
  const [resultData, setResultData] = useState<any[]>();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const shouldLoad = usePageLoaded();
  useEffect(() => {
    if (query == null || !shouldLoad) return;
    if (!shouldLoad) return;

    console.log("b");
    setLoading(true);
    sanityClient
      .fetch(query, params)
      .then((result: any) => {
        console.log("c", result);
        setLoading(false);
        if (result?.length == 0) return setErrorMsg("No items");
        setResultData(result);
      })
      .catch(() => {
        setErrorMsg("No items");
        setLoading(false);
      });
  }, [shouldLoad]);

  return { resultData, loading, errorMsg };
}

export function useKlaviyo(companyId?: string) {
  const KLAVIYO_OVERRIDE = process.env.GATSBY_KLAVIYO_COMPANY_ID;

  const KLAVIYO_COMPANY_ID =
    KLAVIYO_OVERRIDE || companyId || (isEveryday ? "VgpaVM" : "RBUwDr");
  const hasMounted = useHasMounted();

  useEffect(() => {
    if (!isBrowser() || !hasMounted) return;
    const existingScript = document.querySelector(
      `script[src*="klaviyo.js?company_id=${KLAVIYO_COMPANY_ID}"]`,
    );
    if (existingScript) return;

    // Queue only before klaviyo.js loads; the script assigns the real `window.klaviyo`.
    // A custom Proxy here can fight the embed and is unnecessary for the footer form.
    (window as any)._klOnsite = (window as any)._klOnsite || [];

    const script = document.createElement("script");
    script.src = `https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${KLAVIYO_COMPANY_ID}`;
    script.async = true;
    script.type = "text/javascript";
    script.onerror = () => {
      console.error("[Klaviyo] Failed to load onsite script");
    };
    document.body.appendChild(script);
  }, [hasMounted]);
}
